// KRNOS · TIME — Now / Next block, free time left today, and overlapping blocks.
// Reads today's already-merged day list (METIS agenda + KYRS scheduled tasks +
// KRNOS calendar events from useTodayData.dayEntries) and the user's evening
// session time as end-of-day (same bounds v10/orb/shared.tsx `capacity` uses).
// No queries of its own.
import { clockLong, minOf, trunc } from '../../time';
import type { DayEntry } from '../../../../olmps/useTodayData';
import type { OrbWidgetModel, WidgetCtx } from '../types';
import { emptyModel, openAction } from './util';

const BASE = { id: 'krnos', toolId: 'krnos', orderFunction: 'direct', title: 'TIME', deityName: 'KRNOS', agentId: 'krnos' } as const;

const minutesOfDay = (d: Date) => d.getHours() * 60 + d.getMinutes();
const fmtDur = (min: number) => {
  const m = Math.max(0, Math.round(min));
  const h = Math.floor(m / 60);
  return h > 0 ? (m % 60 ? `${h}h ${m % 60}m` : `${h}h`) : `${m}m`;
};

/** Free minutes in [from, end) not covered by any open block (union of intervals). */
function freeMinutes(open: DayEntry[], from: number, end: number): number {
  if (end <= from) return 0;
  const spans = open
    .map((e) => [Math.max(from, minutesOfDay(e.start)), Math.min(end, minutesOfDay(e.end) || 24 * 60)] as const)
    .filter(([a, b]) => b > a)
    .sort((x, y) => x[0] - y[0]);
  let booked = 0;
  let curEnd = from;
  for (const [a, b] of spans) {
    const s = Math.max(a, curEnd);
    if (b > s) {
      booked += b - s;
      curEnd = b;
    }
  }
  return Math.max(0, end - from - booked);
}

export function useKrnosWidget(c: WidgetCtx): OrbWidgetModel {
  try {
    const entries = c.today?.dayEntries ?? [];
    if (entries.length === 0) return emptyModel(BASE, c, 'Nothing scheduled today');

    const nowMs = c.now.getTime();
    const open = entries.filter((e) => !e.completed);
    const nowBlock = open.find((e) => e.start.getTime() <= nowMs && e.end.getTime() > nowMs) ?? null;
    const nextBlock = open.find((e) => e.start.getTime() > nowMs) ?? null;

    // Conflicts: overlapping open blocks (an agenda row and its own KYRS task are one thing, not a clash).
    const conflicts: [DayEntry, DayEntry][] = [];
    for (let i = 0; i < open.length; i++) {
      for (let j = i + 1; j < open.length; j++) {
        const a = open[i];
        const b = open[j];
        if (a.start < b.end && b.start < a.end && !(a.taskId && a.taskId === b.taskId)) conflicts.push([a, b]);
      }
    }

    const dayStart = minOf(c.data?.game?.morningSessionTime ?? '');
    const dayEnd = minOf(c.data?.game?.eveningSessionTime ?? '');
    const free = freeMinutes(open, Math.max(dayStart, minutesOfDay(c.now)), dayEnd);

    const lines: string[] = [];
    if (nowBlock) lines.push(`Now: ${trunc(nowBlock.title, 30)} · until ${clockLong(nowBlock.end)}`);
    if (nextBlock) lines.push(`Next: ${trunc(nextBlock.title, 30)} · ${clockLong(nextBlock.start)}`);
    if (!nowBlock && !nextBlock) lines.push('No more blocks today');
    lines.push(`Free left today: ${fmtDur(free)}`);
    for (const [a, b] of conflicts.slice(0, 2)) lines.push(`Conflict: ${trunc(a.title, 18)} ↔ ${trunc(b.title, 18)}`);

    const first = conflicts[0];
    const summary = first
      ? trunc(`Conflict: ${trunc(first[0].title, 14)} ↔ ${trunc(first[1].title, 14)}`, 40)
      : nextBlock
        ? trunc(`Next: ${trunc(nextBlock.title, 22)} · ${clockLong(nextBlock.start)}`, 40)
        : nowBlock
          ? trunc(`Now: ${trunc(nowBlock.title, 22)} · to ${clockLong(nowBlock.end)}`, 40)
          : `Free ${fmtDur(free)} left`;

    const soon = nextBlock ? nextBlock.start.getTime() - nowMs <= 30 * 60000 : false;
    return {
      ...BASE,
      status: conflicts.length ? 'attention' : nowBlock || soon ? 'active' : 'quiet',
      summary,
      metrics: [
        { label: 'Free left', value: fmtDur(free) },
        { label: 'Blocks', value: open.length },
        ...(conflicts.length ? [{ label: 'Conflicts', value: conflicts.length, tone: 'warning' as const }] : []),
      ],
      alert: first ? `${trunc(first[0].title, 20)} overlaps ${trunc(first[1].title, 20)}` : null,
      lines,
      primaryAction: { label: 'Reschedule', run: () => c.openTool('krnos'), tone: 'gold' },
      secondaryActions: [openAction(c, 'krnos', 'Krnos')],
      signals: { active: !!nowBlock, alerted: conflicts.length > 0 },
    };
  } catch (e) {
    console.warn('[orb widget] krnos model failed', e);
    return emptyModel(BASE, c, 'Nothing scheduled today');
  }
}
